/**
 * templateDiff — pure helpers for the Template Studio's TSE-3 polish
 * capabilities (P2 version history + P3 audit log + P4 diff dialog).
 *
 * - `diffFieldPaths(prev, next)` returns a sorted list of dot-paths
 *   for fields that meaningfully changed between two objects. Array
 *   indices use `[N]` notation (e.g. `sections[3].headingOverride`).
 *   Used by the audit log so the Activity tab can render
 *   "You changed: theme.primaryColor, sections[2].visible" without
 *   re-deriving the diff per row.
 *
 * - `stripHistory(template)` returns the template minus its
 *   `versionHistory` field so a snapshot in `versionHistory[].snapshot`
 *   can't recurse (cap=10 entries each holding 10 holding…).
 *
 * - `pushVersionEntry(prior, savedBy, savedAt, ?email)` returns the
 *   next `versionHistory[]` capped at 10 entries, newest first.
 *
 * Pure functions only — no React, no Firestore, no Zustand.
 */
import type { ReportTemplate } from '../types/reportTemplate';

const HISTORY_CAP = 10;

/**
 * Strip `versionHistory` off a template so a snapshot can be pushed
 * onto another template's `versionHistory[]` without nested history.
 */
export function stripHistory(t: ReportTemplate): Omit<ReportTemplate, 'versionHistory'> {
  // Destructure away versionHistory; rest is the snapshot.
  const { versionHistory: _omit, ...rest } = t;
  return rest;
}

/**
 * Build the next `versionHistory[]` to write onto a template that's
 * about to be persisted. Pass the PRIOR template (pre-mutate) — its
 * snapshot is pushed onto the head, and the result is capped at
 * `HISTORY_CAP` entries, newest first.
 *
 * Returns an empty array when `prior` is null (a fresh template has
 * no prior state to record).
 */
export function pushVersionEntry(
  prior: ReportTemplate | null,
  savedBy: string,
  savedAt: string,
  savedByEmail?: string,
): NonNullable<ReportTemplate['versionHistory']> {
  if (!prior) return [];
  const entry = {
    version: prior.version ?? 1,
    snapshot: stripHistory(prior),
    savedAt,
    savedBy,
    ...(savedByEmail ? { savedByEmail } : {}),
  };
  const next = [entry, ...(prior.versionHistory ?? [])].slice(0, HISTORY_CAP);
  return next;
}

/**
 * Recursively diff two values, returning a sorted list of dot-paths
 * for the fields that changed. Uses JSON equality for nested objects
 * — a perfect-enough check for template comparisons because every
 * field in `ReportTemplate` is JSON-safe.
 *
 * Excluded by default: `updatedAt`, `versionHistory`, `version` —
 * these mutate on every save and would dominate the diff list with
 * noise. Callers that want them included can post-process.
 */
export function diffFieldPaths(
  prev: Record<string, unknown> | null | undefined,
  next: Record<string, unknown> | null | undefined,
  prefix: string = '',
  exclude: ReadonlySet<string> = DEFAULT_EXCLUDE_TOP_LEVEL,
): string[] {
  const out = new Set<string>();
  const a = prev ?? {};
  const b = next ?? {};
  const allKeys = new Set<string>([...Object.keys(a), ...Object.keys(b)]);
  for (const key of allKeys) {
    if (prefix === '' && exclude.has(key)) continue;
    const path = prefix === '' ? key : `${prefix}.${key}`;
    const va = (a as Record<string, unknown>)[key];
    const vb = (b as Record<string, unknown>)[key];
    if (va === vb) continue;
    if (isPlainObject(va) && isPlainObject(vb)) {
      const nested = diffFieldPaths(va, vb, path, exclude);
      nested.forEach(p => out.add(p));
      continue;
    }
    if (Array.isArray(va) && Array.isArray(vb)) {
      // Array diff: index-by-index. Use `[i]` syntax in the path.
      const max = Math.max(va.length, vb.length);
      let changedAny = false;
      for (let i = 0; i < max; i++) {
        const ai = va[i];
        const bi = vb[i];
        if (ai === bi) continue;
        if (isPlainObject(ai) && isPlainObject(bi)) {
          const nested = diffFieldPaths(ai, bi, `${path}[${i}]`, exclude);
          if (nested.length > 0) {
            nested.forEach(p => out.add(p));
            changedAny = true;
          }
        } else if (JSON.stringify(ai) !== JSON.stringify(bi)) {
          out.add(`${path}[${i}]`);
          changedAny = true;
        }
      }
      // If lengths differ but content didn't materially diverge at
      // typed elements, still flag the parent so the consumer knows.
      if (!changedAny && va.length !== vb.length) out.add(path);
      continue;
    }
    if (JSON.stringify(va) !== JSON.stringify(vb)) out.add(path);
  }
  return Array.from(out).sort();
}

const DEFAULT_EXCLUDE_TOP_LEVEL: ReadonlySet<string> = new Set([
  'updatedAt',
  'createdAt',
  'versionHistory',
  // We don't exclude `version` here because callers usually WANT to
  // see that a save bumped version 4 → 5; but diffFieldPaths is
  // commonly called BEFORE the version bump, so the field would be
  // identical anyway.
]);

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v) && Object.getPrototypeOf(v) === Object.prototype;
}
